/**
 * What KIND of glTF is this, read from the header alone.
 *
 * The drop door used to route on the file EXTENSION: every `.glb` went to the
 * model importer. That is wrong for a file this app itself writes — the
 * animation-only clip artifact (ADR-016's Artifact B) is a `.glb` carrying
 * animations and deliberately NO skin, so dropping one back in reached
 * `convertRig` and refused with "contains no skinned skeleton", which is true,
 * unhelpful, and reads as "the app cannot open its own export".
 *
 * A clip artifact is not a broken model. It is a clip source, and routing it
 * as one is both the honest answer and the useful one.
 *
 * Header-only on purpose: this runs before the loader, on bytes that may be
 * a hundred megabytes of texture, and it reads the JSON chunk and stops.
 *
 * Depends on: nothing. Depended on by: the app's drop door.
 */

/** GLB magic, and the JSON chunk type — little-endian, as the container stores them. */
const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;

export interface GlbShape {
  /** A skinned mesh's skeleton lives here; without one there is no rig to import. */
  readonly hasSkins: boolean;
  readonly animationCount: number;
  readonly meshCount: number;
}

/**
 * Read a GLB's shape, or null when the bytes are not a GLB at all (a `.vrm`
 * is a GLB and reads fine; an FBX or a truncated file returns null and the
 * caller keeps its existing behaviour).
 */
export function glbShape(bytes: ArrayBuffer): GlbShape | null {
  if (bytes.byteLength < 20) return null;
  const view = new DataView(bytes);
  if (view.getUint32(0, true) !== GLB_MAGIC) return null;
  const total = Math.min(view.getUint32(8, true), bytes.byteLength);
  let offset = 12;
  while (offset + 8 <= total) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    if (type === CHUNK_JSON) {
      const end = Math.min(offset + 8 + length, bytes.byteLength);
      let json: unknown;
      try {
        json = JSON.parse(new TextDecoder().decode(new Uint8Array(bytes, offset + 8, end - (offset + 8))));
      } catch {
        return null;
      }
      const doc = json as { skins?: unknown[]; animations?: unknown[]; meshes?: unknown[] };
      return {
        hasSkins: Array.isArray(doc.skins) && doc.skins.length > 0,
        animationCount: Array.isArray(doc.animations) ? doc.animations.length : 0,
        meshCount: Array.isArray(doc.meshes) ? doc.meshes.length : 0,
      };
    }
    offset += 8 + length;
  }
  return null;
}
